'use client'

import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'

type Props = {
  /** Unidades en el carrito (suma de cantidades). 0 = la barra no existe. */
  count: number
  /** Subtotal del carrito: la misma cifra que el carrito y el «Ver mi pedido · $X» del upsell. */
  total: number
  accentColor: string
  /** Otra hoja ocupa la pantalla (el pedido abierto, el upsell): la barra se aparta. */
  hidden: boolean
  onOpen: () => void
}

/**
 * Barra fija «Ver mi pedido» al pie de la tienda online (pedido de Samuel,
 * 2026-10-19): con artículos en el carrito, el único lugar para seguir era el
 * carrito chico del header, y quien no sabe comprar en línea no lo encuentra.
 *
 * Solo aparece con algo en el carrito. Publica su alto en `--ms-order-bar-h`
 * (0 sin barra) para que la página deje espacio abajo y el sello «Powered by
 * Redy» (RedyBrandBadge, fixed) suba y no quede tapado.
 */
export function OrderBar({ count, total, accentColor, hidden, onOpen }: Props) {
  const t = useTranslations('public.microsite')
  const ref = useRef<HTMLDivElement>(null)
  const visible = count > 0 && !hidden

  // Un salto corto cada vez que el carrito crece: el cliente ve que su pedido cambió.
  const [bump, setBump] = useState(false)
  const prevCount = useRef(count)
  useEffect(() => {
    const grew = count > prevCount.current
    prevCount.current = count
    if (!grew) return
    setBump(true)
    const id = setTimeout(() => setBump(false), 350)
    return () => clearTimeout(id)
  }, [count])

  useEffect(() => {
    const root = document.documentElement
    const el = ref.current
    if (!visible || !el) {
      root.style.setProperty('--ms-order-bar-h', '0px')
      return
    }
    const publish = () => root.style.setProperty('--ms-order-bar-h', `${Math.round(el.offsetHeight)}px`)
    publish()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null
    ro?.observe(el)
    return () => {
      ro?.disconnect()
      root.style.setProperty('--ms-order-bar-h', '0px')
    }
  }, [visible])

  if (!visible) return null

  return (
    <div
      ref={ref}
      data-testid="order-bar"
      className="fixed inset-x-0 bottom-0 z-30 px-4 pt-2"
      style={{
        paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
        background: 'linear-gradient(to top, var(--ms-bg, #fff) 70%, transparent)',
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('orderBarAria', { count, total: `$${total.toFixed(2)}` })}
        className={`mx-auto flex w-full max-w-xl items-center gap-3 rounded-full px-4 py-3.5 text-white shadow-lg transition-transform active:scale-[0.99] ${bump ? 'scale-[1.03]' : ''}`}
        style={{ backgroundColor: accentColor || 'var(--ms-accent)' }}
      >
        <span className="flex h-7 min-w-[28px] items-center justify-center rounded-full bg-white/25 px-2 text-sm font-bold">
          {count}
        </span>
        <span className="flex-1 text-center text-base font-bold">{t('orderBarView')}</span>
        <span className="text-base font-bold tabular-nums">${total.toFixed(2)}</span>
      </button>
    </div>
  )
}
